import React from "react";
import cn from "classnames";

import ResponsiveTable from "../../containers/responsive-table";
import Row from "../../containers/responsive-table/row";
import TBody from "../../containers/responsive-table/tbody";
import HeadCell from "../../containers/responsive-table/head-cell";
import BodyCell from "../../containers/responsive-table/body-cell";
import { decimalsRound } from "../../services/utils";
import MarketButton from "../../containers/market-button";
import Thead from "../../containers/responsive-table/thead";
import styled from 'styled-components';

const getBackgroundVariant = (value, itemRound, currentRound) => {
  if (itemRound === currentRound && value > 0) {
    return "highlighted";
  } else if (value <= 0) {
    return "disabled";
  } else {
    return "default";
  }
};

const TableWrapper = styled.div`
  padding: 20px;

  ${p => p.padding && `
    padding: ${p.padding};
  `}
`;

const TableHeaderText = styled.div`
  font-family: 'Mukta Vaani';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const FinancialTargets = props => {
  const { round, financialTargets, showTableOnly, showFullTable, hideTitle, padding, onHelpIconClick } = props;

  const businessUrl = `analysis/bcase?round=${round}`;
  const analysisUrl = `ceo/analysis/market?round=${round}`;

  return !showTableOnly ? (
    <div className="financial-targets">
      <div className="financial-targets__col">
        <h3 className="financial-targets__title">Financial Targets</h3>
        <ResponsiveTable>
          <Thead>
            <Row>
              <HeadCell iconType={"sorter"} cellSize="small">
                Year
              </HeadCell>
              <HeadCell iconType={"help"}>Market Share</HeadCell>
              <HeadCell iconType={"help"}>Revenues</HeadCell>
              <HeadCell iconType={"help"}>Gross Margins</HeadCell>
              <HeadCell iconType={"help"}>Share Price</HeadCell>
              <HeadCell iconType={"help"}>ROE</HeadCell>
              <HeadCell iconType={"help"}>ROA</HeadCell>
              <HeadCell iconType={"help"}>EPS</HeadCell>
            </Row>
          </Thead>
          <TBody>
            {financialTargets.map((item, index) => (
              <Row key={`financial-targets-row-${index}`} isBody>
                <BodyCell
                  alignText="center"
                  cellSize="small"
                  backgroundVariant={getBackgroundVariant(
                    item.round_number,
                    item.round_number,
                    round
                  )}
                >
                  {item.round_number}
                </BodyCell>
                <BodyCell
                  backgroundVariant={getBackgroundVariant(
                    item.market_share,
                    item.round_number,
                    round
                  )}
                >
                  {item.market_share > 0
                    ? `${decimalsRound(item.market_share * 100, 2)}%`
                    : "--"}
                </BodyCell>
                <BodyCell
                  backgroundVariant={getBackgroundVariant(
                    item.net_revenues,
                    item.round_number,
                    round
                  )}
                >
                  {item.net_revenues > 0
                    ? `$${decimalsRound(item.net_revenues / 1000000, 2)}M`
                    : "--"}
                </BodyCell>
                <BodyCell
                  backgroundVariant={getBackgroundVariant(
                    item.gross_margin,
                    item.round_number,
                    round
                  )}
                >
                  {item.gross_margin > 0
                    ? `${decimalsRound(item.gross_margin * 100, 2)}%`
                    : "--"}
                </BodyCell>
                <BodyCell
                  backgroundVariant={getBackgroundVariant(
                    item.stock_price,
                    item.round_number,
                    round
                  )}
                >
                  {item.stock_price > 0
                    ? `$${decimalsRound(item.stock_price, 2)}`
                    : "--"}
                </BodyCell>
                <BodyCell
                  backgroundVariant={getBackgroundVariant(
                    item.roe,
                    item.round_number,
                    round
                  )}
                >
                  {item.roe > 0 ? `${decimalsRound(item.roe * 100, 2)}%` : "--"}
                </BodyCell>
                <BodyCell
                  backgroundVariant={getBackgroundVariant(
                    item.roa,
                    item.round_number,
                    round
                  )}
                >
                  {item.roa > 0 ? `${decimalsRound(item.roa * 100, 2)}%` : "--"}
                </BodyCell>
                <BodyCell
                  backgroundVariant={getBackgroundVariant(
                    item.earnings_per_share,
                    item.round_number,
                    round
                  )}
                >
                  {item.earnings_per_share > 0
                    ? `$${decimalsRound(item.earnings_per_share, 2)}`
                    : "--"}
                </BodyCell>
              </Row>
            ))}
          </TBody>
        </ResponsiveTable>
      </div>
      <div className={cn("financial-targets__info", "financial-targets__col")}>
        <h3 className="financial-targets__title">Market Research</h3>
        <div className="financial-targets__text">
          Once you understand the Financial Targets, you’ll need to review the
          Business Case and the Customer Analysis to determine which customer(s)
          you want to serve to help you meet your Financial Targets.
        </div>
        <MarketButton
          size="medium"
          variant="info"
          type="outlined"
          href={businessUrl}
        >
          Read Business Case
        </MarketButton>
        <MarketButton
          size="medium"
          variant="info"
          type="outlined"
          href={analysisUrl}
        >
          View Customer Analysis
        </MarketButton>
      </div>
    </div>
  ) : (
    <TableWrapper padding={padding}>
      {!hideTitle && <TableHeaderText>Financial Metrics &amp; Targets</TableHeaderText>}
      <ResponsiveTable>
        <Thead>
          <Row>
            <HeadCell iconType="sort" cellSize="small">
              Year
            </HeadCell>
            <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("Market Share")}>Market Share</HeadCell>
            <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("Revenues")}>Revenues</HeadCell>
            <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("Gross Margins")}>Gross Margins</HeadCell>
            <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("Share Price")}>Share Price</HeadCell>
            {showFullTable && <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("ROE")}>ROE</HeadCell>}
            {showFullTable && <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("ROA")}>ROA</HeadCell>}
            {showFullTable && <HeadCell iconType="help" onHelpIconClick={() => onHelpIconClick("EPS")}>EPS</HeadCell>}
          </Row>
        </Thead>
        <TBody>
          {financialTargets.map((item, index) => (
            <Row key={`financial-targets-row-${index}`} isBody>
              <BodyCell
                alignText="center"
                cellSize="small"
                backgroundVariant="default"
              >
                {item.round_number}
              </BodyCell>
              <BodyCell
                backgroundVariant={getBackgroundVariant(
                  item.market_share,
                  item.round_number,
                  round
                )}
              >
                {item.market_share > 0
                  ? `${decimalsRound(item.market_share * 100, 2)}%`
                  : "--"}
              </BodyCell>
              <BodyCell
                backgroundVariant={getBackgroundVariant(
                  item.net_revenues,
                  item.round_number,
                  round
                )}
              >
                {item.net_revenues > 0
                  ? `$${decimalsRound(item.net_revenues / 1000000, 2)}M`
                  : "--"}
              </BodyCell>
              <BodyCell
                backgroundVariant={getBackgroundVariant(
                  item.gross_margin,
                  item.round_number,
                  round
                )}
              >
                {item.gross_margin > 0
                  ? `${decimalsRound(item.gross_margin * 100, 2)}%`
                  : "--"}
              </BodyCell>
              <BodyCell
                backgroundVariant={getBackgroundVariant(
                  item.stock_price,
                  item.round_number,
                  round
                )}
              >
                {item.stock_price > 0
                  ? `$${decimalsRound(item.stock_price, 2)}`
                  : "--"}
              </BodyCell>
              {showFullTable && <BodyCell
                backgroundVariant={getBackgroundVariant(
                  item.roe,
                  item.round_number,
                  round
                )}
              >
                {item.roe > 0 ? `${decimalsRound(item.roe * 100, 2)}%` : "--"}
              </BodyCell>}
              {showFullTable && <BodyCell
                backgroundVariant={getBackgroundVariant(
                  item.roa,
                  item.round_number,
                  round
                )}
              >
                {item.roa > 0 ? `${decimalsRound(item.roa * 100, 2)}%` : "--"}
              </BodyCell>}
              {showFullTable && <BodyCell
                backgroundVariant={getBackgroundVariant(
                  item.earnings_per_share,
                  item.round_number,
                  round
                )}
              >
                {item.earnings_per_share > 0
                  ? `$${decimalsRound(item.earnings_per_share, 2)}`
                  : "--"}
              </BodyCell>}
            </Row>
          ))}
        </TBody>
      </ResponsiveTable>
    </TableWrapper>
  );
};

export default FinancialTargets;



// WEBPACK FOOTER //
// ./src/components/dashboard/FinancialTargets.js